import { useContext, useEffect, useRef } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import { TradingContext } from '../../contexts/TradingContext';
import { useTradingRulesConfig } from '../../contexts/TradingRulesConfigContext';
import { useRealtimeAccountMetrics } from '../../hooks/useRealtimeAccountMetrics';
import { useUpdateAppSettings } from '../../hooks/useBackend';

/**
 * Applies onboarding rule preferences once a broker account is selected:
 * - `defaultTradingRulesTemplate` + `applyDefaultTradingRulesTemplateOnce` —
 *   durable user template; one-shot apply when the account may have been
 *   created before the template was saved (connect race). New accounts are
 *   also seeded server-side on first GET /trading_rules_config.
 * - `pendingDailyLossRule` — Activate Protection single-rule path (legacy).
 *
 * Renders nothing. Mount once near the top of the authenticated app shell.
 */
export default function PendingDailyLossRuleApplier() {
  const { user, refreshUserSettings } = useContext(AuthContext);
  const { selectedAccount } = useContext(TradingContext);
  const { settings, loading, saveSettings } = useTradingRulesConfig();
  const { accountValue, isReady } = useRealtimeAccountMetrics();
  const { updateAppSettings } = useUpdateAppSettings();
  const applyingRef = useRef(false);

  const template = user?.appSettings?.defaultTradingRulesTemplate;
  const applyOnce = user?.appSettings?.applyDefaultTradingRulesTemplateOnce === true;
  const pendingDaily = user?.appSettings?.pendingDailyLossRule;

  useEffect(() => {
    if (applyingRef.current) return;
    if (!user?.token || !selectedAccount?.AccountID) return;
    if (loading || !settings) return;

    const clearFlags = async (extra = {}) => {
      await updateAppSettings(user.token, {
        applyDefaultTradingRulesTemplateOnce: false,
        ...extra,
      });
      if (refreshUserSettings) await refreshUserSettings();
    };

    const applyTemplate = async () => {
      if (!applyOnce) return false;
      if (!template?.rule_thresholds && !template?.enforcement_config) {
        await clearFlags();
        return false;
      }

      applyingRef.current = true;
      try {
        await saveSettings({
          rule_thresholds: {
            ...(settings.rule_thresholds || {}),
            ...(template.rule_thresholds || {}),
          },
          enforcement_config: {
            ...(settings.enforcement_config || {}),
            ...(template.enforcement_config || {}),
          },
          additional_rules: {
            ...(settings.additional_rules || {}),
            ...(template.additional_rules || {}),
          },
        });
        await clearFlags({ pendingDailyLossRule: null });
        return true;
      } catch (err) {
        console.error('[PendingDailyLossRuleApplier] Failed to apply defaultTradingRulesTemplate:', err);
        return false;
      } finally {
        applyingRef.current = false;
      }
    };

    const applyDailyOnly = async () => {
      if (!pendingDaily) return false;
      if (pendingDaily.mode === 'percent' && (!isReady || !accountValue)) return false;

      const rawDollarThreshold =
        pendingDaily.mode === 'percent'
          ? (Number(accountValue) * Number(pendingDaily.value)) / 100
          : Number(pendingDaily.value);
      const dollarThreshold = Math.round(rawDollarThreshold * 100) / 100;
      if (!dollarThreshold || !Number.isFinite(dollarThreshold) || dollarThreshold <= 0) return false;

      applyingRef.current = true;
      try {
        await saveSettings({
          rule_thresholds: {
            ...settings.rule_thresholds,
            max_loss_per_day: -Math.abs(dollarThreshold),
            max_loss_per_day_mode: pendingDaily.mode,
            max_loss_per_day_percent:
              pendingDaily.mode === 'percent' ? Number(pendingDaily.value) : null,
          },
          enforcement_config: {
            ...settings.enforcement_config,
            max_loss_per_day: {
              enabled: true,
              enforce: pendingDaily.enforcement === 'enforce',
              alert: pendingDaily.enforcement === 'track',
            },
          },
        });
        await clearFlags({ pendingDailyLossRule: null });
        return true;
      } catch (err) {
        console.error('[PendingDailyLossRuleApplier] Failed to apply pending daily loss rule:', err);
        return false;
      } finally {
        applyingRef.current = false;
      }
    };

    (async () => {
      const didTemplate = await applyTemplate();
      if (!didTemplate) await applyDailyOnly();
    })();
  }, [
    template,
    applyOnce,
    pendingDaily,
    user?.token,
    user?.appSettings,
    selectedAccount?.AccountID,
    loading,
    settings,
    accountValue,
    isReady,
    saveSettings,
    updateAppSettings,
    refreshUserSettings,
  ]);

  return null;
}
